import { memo } from 'react';

/**
 * ChatListItem - Single chat row in sidebar
 * Apple Messages-inspired design with light/dark mode support
 */

function ChatListItem({ chat, isSelected, onClick, currentUserId }) {
  // Format last message time
  const formatTime = (timestamp) => {
    if (!timestamp) return '';

    const date = timestamp?.toDate?.() || new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;

    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  // Get chat icon based on type
  const getChatIcon = () => {
    switch (chat.type) {
      case 'org_channel':
        return <span className="text-white font-semibold text-xs">#</span>;
      case 'group':
        return (
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        );
      case 'meeting':
        return (
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        );
      default:
        return (
          <span className="text-white font-medium text-sm">
            {chat.name?.[0]?.toUpperCase() || '?'}
          </span>
        );
    }
  };

  // Get background color based on type
  const getIconBg = () => {
    switch (chat.type) {
      case 'org_channel':
        return 'bg-gradient-to-br from-purple-500 to-blue-500';
      case 'group':
        return 'bg-gradient-to-br from-green-500 to-emerald-500';
      case 'meeting':
        return 'bg-gradient-to-br from-orange-500 to-red-500';
      default:
        return 'bg-gradient-to-br from-[#06c] to-[#0055d4]';
    }
  };

  // Get display name
  const getDisplayName = () => {
    if (chat.type === 'org_channel') {
      return `#${chat.channelSlug || chat.name}`;
    }
    return chat.name || 'Chat';
  };

  // Get last message preview
  const getLastMessagePreview = () => {
    if (!chat.lastMessage) return 'No messages yet';

    const prefix = chat.lastMessage.senderId === currentUserId ? 'You: ' : '';
    const text = chat.lastMessage.text || '';
    const maxLength = 35;

    return prefix + (text.length > maxLength ? text.substring(0, maxLength) + '...' : text);
  };

  return (
    <button
      onClick={() => onClick(chat)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(chat);
        }
      }}
      aria-label={`Chat with ${getDisplayName()}${chat.hasUnread ? ', has unread messages' : ''}`}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#06c] ${
        isSelected
          ? 'bg-[#06c]/10 dark:bg-[#06c]/20'
          : 'hover:bg-[#f5f5f7] dark:hover:bg-white/5'
      }`}
    >
      {/* Avatar */}
      <div className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm ${getIconBg()}`}>
        {getChatIcon()}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className={`font-medium truncate text-[15px] ${
            chat.hasUnread
              ? 'text-[#1d1d1f] dark:text-white'
              : 'text-[#1d1d1f] dark:text-white/90'
          }`}>
            {getDisplayName()}
          </span>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className={`text-xs ${
              chat.hasUnread ? 'text-[#06c]' : 'text-[#86868b]'
            }`}>
              {formatTime(chat.lastMessage?.timestamp)}
            </span>
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 mt-0.5">
          <p className={`text-[13px] truncate ${
            chat.hasUnread
              ? 'text-[#1d1d1f] dark:text-white/80 font-medium'
              : 'text-[#86868b]'
          }`}>
            {getLastMessagePreview()}
          </p>
          {/* Unread badge */}
          {chat.hasUnread && (
            <span className="min-w-[20px] h-[20px] px-1.5 bg-[#06c] text-white text-[11px] font-bold rounded-full flex items-center justify-center flex-shrink-0">
              1
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

export default memo(ChatListItem);
